import React, { useContext, useEffect, useState } from "react";
import { Button, Collapse, FormControlLabel, Grid, Paper, Switch, useTheme } from "@mui/material";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FilterAltRounded, ReplayRounded, SearchRounded } from "@mui/icons-material";
import { useSetRecoilState } from "recoil";
import { filterParams } from "../../state";
import { ISearchExpiredPracticesForm } from "../../models/models";
import { AMOUNT_SEARCH_FILTER_KEYS, BOOLEAN_SEARCH_FILTER_KEYS, DATE_FORMAT_FILTER_KEYS, EXPIRED_PRACTICES_INITIAL_VALUE, EXPIRED_PRACTICES_QUERY_KEY, MULTIPLE_AUTOCOMPLETE_SEARCH_FILTER_KEYS, OFFSET_DATE_TIME_FILTERS_KEYS, PracticeStatus, SEARCH_EXPIRED_PRACTICES_FORM_VALIDATION_SCHEMA } from "../../config";
import { FormAutocomplete, FormDateTimePicker, FormNumberInput, FormTextField } from "../../common/form-fields-wrappers";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { findRecoveryCompany, getPracticesDomains } from "../../services/http.services";
import SubstatusFilter from "./filter/SubstatusFilter";
import { yupResolver } from "@hookform/resolvers/yup";
import TenantForm from "../../../booking/components/multitenant/TenantForm";
// import DealerForm from "../../../booking/components/multitenant/DealerForm";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { filterObject } from "../../../network/utils";

const ExpiredPracticesListFilter = () => {
    const theme = useTheme();
    const { t } = useTranslation(['expiredManagement']);
    const { globalData } = useContext(GlobalContext);

    const [filterExpandedState, setFilterExpandedState] = useState(false);

    const setFilterParams = useSetRecoilState(filterParams);

    const searchExpiredPracticesForm = useForm<ISearchExpiredPracticesForm>({
        defaultValues: EXPIRED_PRACTICES_INITIAL_VALUE,
        resolver: yupResolver(SEARCH_EXPIRED_PRACTICES_FORM_VALIDATION_SCHEMA)
    });

    const tenants = useWatch({
        control: searchExpiredPracticesForm.control,
        name: "tenants",
    });

    useEffect(() => {
        if (globalData.user.legalEntities.length === 1) {
            searchExpiredPracticesForm.setValue('tenants', globalData.user.legalEntities.map((elem: any) => elem.name as string));
        } else if (globalData.user.legalEntities.length > 1) {
            searchExpiredPracticesForm.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all').map((elem: any) => elem.name as string));
            // searchExpiredPracticesForm.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all').map((elem:any) => elem.name as string));
        }

        const data = searchExpiredPracticesForm.getValues();
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(SEARCH_EXPIRED_PRACTICES_FORM_VALIDATION_SCHEMA[x])));
    }, []);

    /** Search Actions*/
    const handleResetForm = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        searchExpiredPracticesForm.reset();

        handleSearch(searchExpiredPracticesForm.getValues());
    }

    const handleHideViewFilter = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        setFilterExpandedState(!filterExpandedState);
    }

    const handleSearch = (formData: ISearchExpiredPracticesForm): void => {
        const filters: any = {};
        for (const [key, value] of Object.entries(formData)) {
            if (!!value || BOOLEAN_SEARCH_FILTER_KEYS.includes(key)) {
                if (Array.isArray(value) && value.length === 0) {
                    continue;
                }

                if (MULTIPLE_AUTOCOMPLETE_SEARCH_FILTER_KEYS.includes(key)) { //IGenericOption[]
                    filters[key] = value.map((x: any) => x.code);
                }
                else if (DATE_FORMAT_FILTER_KEYS.includes(key)) {
                    filters[key] = dayjs(value)?.utc().format("YYYY-MM-DD HH:mm") || null;
                }
                else if (OFFSET_DATE_TIME_FILTERS_KEYS.includes(key)) {
                    filters[key] = dayjs(value)?.toISOString() || null;
                }
                else if (AMOUNT_SEARCH_FILTER_KEYS.includes(key)) {
                    filters[key] = Math.round(value * 100);
                }
                else if (key === 'rentalType') {
                    filters[key] = value.code;
                }
                else if (key === 'recoveryCompany') {
                    filters[key] = value.map((x: any) => x.name);
                }
                else {
                    filters[key] = value;
                }
            }
        }

        setFilterParams(filters);
        setFilterExpandedState(false);
    }

    const domainsQuery = useQuery({
        queryKey: [EXPIRED_PRACTICES_QUERY_KEY.DOMAINS],
        queryFn: getPracticesDomains,
    });

    const findRecoveryCompaniesQuery = useQuery({
        queryKey: [EXPIRED_PRACTICES_QUERY_KEY.FIND_RECOVERY_COMPANIES],
        queryFn: findRecoveryCompany
    });

    return (
        <form noValidate onSubmit={searchExpiredPracticesForm.handleSubmit(handleSearch)}>
            <Paper elevation={1} sx={{ p: theme.spacing(2), mb: theme.spacing(2) }}>
                <FormProvider {...searchExpiredPracticesForm}>
                    <Grid container columnSpacing={5} alignItems="center">

                        <Grid item xs={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
                            <FormAutocomplete<ISearchExpiredPracticesForm>
                                name="rentalType"
                                control={searchExpiredPracticesForm.control}
                                label={t('searchExpired.form.rentalType')}
                                fullWidth
                                options={domainsQuery.data?.rentalTypes || []}
                                loading={domainsQuery.isLoading}
                                isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                getOptionLabel={(opt: any) => opt?.label}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
                            <FormAutocomplete<ISearchExpiredPracticesForm>
                                name="statuses"
                                control={searchExpiredPracticesForm.control}
                                label={t('searchExpired.form.statuses')}
                                fullWidth
                                options={domainsQuery.data?.statuses || []}
                                loading={domainsQuery.isLoading}
                                isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                getOptionLabel={(opt: any) => opt?.label}
                                multiple
                                onChange={(_event, value) => {

                                    if (!!value) {
                                        searchExpiredPracticesForm.setValue("hideClosed", !value.map((x: any) => x.code).includes(PracticeStatus.closed));
                                        searchExpiredPracticesForm.setValue("hideClosedLawsuit", !value.map((x: any) => x.code).includes(PracticeStatus.closedToLawsuit));

                                    } else {
                                        searchExpiredPracticesForm.setValue("hideClosed", true);
                                        searchExpiredPracticesForm.setValue("hideClosedLawsuit", true);
                                    }

                                    //GTODO: Valutare se gestire diversamente
                                    searchExpiredPracticesForm.setValue("substatuses", []);

                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
                            <FormTextField<ISearchExpiredPracticesForm>
                                name='raCode'
                                control={searchExpiredPracticesForm.control}
                                label={t('searchExpired.form.raCode')}
                                fullWidth
                            />
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={6} md={3} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                            <Button variant={'text'}
                                color={"accent"}
                                startIcon={<ReplayRounded />}
                                onClick={handleResetForm}
                                sx={{ mx: theme.spacing(0.5) }}>
                                {t('common.button.reset')}
                            </Button>

                            <Button variant={'text'}
                                color={"accent"}
                                startIcon={<FilterAltRounded />}
                                onClick={handleHideViewFilter}
                                sx={{ mx: theme.spacing(0.5) }}>
                                {
                                    filterExpandedState ? t('common.button.close') : t('common.button.advancedFilters')
                                }
                            </Button>

                            <Button color={"accent"}
                                size={"icon"}
                                type="submit"
                                sx={{ mx: theme.spacing(0.5) }}>
                                <SearchRounded />
                            </Button>
                        </Grid>


                    </Grid>
                    <Collapse in={filterExpandedState} timeout="auto" sx={{ width: '100%' }}>
                        <Grid container columnSpacing={5} rowSpacing={2} pt={2}>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerSapId'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerSapId')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerFiscalCode'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerFiscalCode')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerLastName'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerLastName')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerFirstName'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerFirstName')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerEmail'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerEmail')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='customerMobile'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.customerMobile')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='companyName'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.companyName')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="createdDatetimeFrom"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.createdDatetimeFrom')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="createdDatetimeTo"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.createdDatetimeTo')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name="createdBy"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.createdBy')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <SubstatusFilter data={domainsQuery.data} isLoading={domainsQuery.isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="effectiveAcrissList"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.effectiveAcrissList')}
                                    fullWidth
                                    options={domainsQuery.data?.effectiveAcrisses || []}
                                    loading={domainsQuery.isLoading}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="vehicleModels"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.vehicleModel')}
                                    fullWidth
                                    options={domainsQuery.data?.vehicleModels || []}
                                    loading={domainsQuery.isLoading}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='plate'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.plate')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="recoveryCompany"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.recoveryCompany')}
                                    fullWidth
                                    options={findRecoveryCompaniesQuery.data?.data || []}
                                    loading={findRecoveryCompaniesQuery.isLoading}
                                    isOptionEqualToValue={(option: any, value: any) => option?.name === value?.name}
                                    getOptionLabel={(opt: any) => opt?.name}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="lastAction"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.lastAction')}
                                    fullWidth
                                    options={domainsQuery.data?.lastActions || []}
                                    loading={domainsQuery.isLoading}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name='bookingCode'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.bookingCode')}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="segments"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.segments')}
                                    fullWidth
                                    options={domainsQuery.data?.segments || []}
                                    loading={domainsQuery.isLoading}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="categories"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.categories')}
                                    fullWidth
                                    options={domainsQuery.data?.categories || []}
                                    loading={domainsQuery.isLoading}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="products"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.products')}
                                    fullWidth
                                    options={domainsQuery.data?.products || []}
                                    loading={domainsQuery.isLoading}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.label}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="rates"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.rates')}
                                    fullWidth
                                    options={domainsQuery.data?.rates || []}
                                    loading={domainsQuery.isLoading}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.label}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="billingAcrissList"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.billingAcrissList')}
                                    fullWidth
                                    options={domainsQuery.data?.billingAcrisses || []}
                                    loading={domainsQuery.isLoading}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="effectiveCheckOutDateFrom"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.effectiveCheckOutDateFrom')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="effectiveCheckOutDateTo"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.effectiveCheckOutDateTo')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="effectiveCheckOutLocation"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.effectiveCheckOutLocation')}
                                    fullWidth
                                    options={domainsQuery.data?.effectiveCheckOutLocations || []}
                                    loading={domainsQuery.isLoading}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.label}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="expectedCheckInFrom"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.expectedCheckInFrom')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<ISearchExpiredPracticesForm>
                                    name="expectedCheckInTo"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.expectedCheckInTo')}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<ISearchExpiredPracticesForm>
                                    name="expectedCheckInLocation"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.expectedCheckInLocation')}
                                    fullWidth
                                    options={domainsQuery.data?.expectedCheckInLocations || []}
                                    loading={domainsQuery.isLoading}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.label}
                                    multiple
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name="competanceAmountFrom"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.competanceAmountFrom')}
                                    type="number"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<ISearchExpiredPracticesForm>
                                    name="competanceAmountTo"
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.competanceAmountTo')}
                                    type="number"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormNumberInput<ISearchExpiredPracticesForm>
                                    name='delaydaysFrom'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.delaydaysFrom')}
                                    minValue={0}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormNumberInput<ISearchExpiredPracticesForm>
                                    name='delaydaysTo'
                                    control={searchExpiredPracticesForm.control}
                                    label={t('searchExpired.form.delaydaysTo')}
                                    minValue={0}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3}>
                                <FormProvider {...searchExpiredPracticesForm}>
                                    <TenantForm
                                        name="tenants"
                                    />
                                </FormProvider>
                            </Grid>

                            {/* <Grid item xs={12} sm={6} lg={3}>
                                    <FormProvider {...searchExpiredPracticesForm}>
                                        <DealerForm
                                            name="dealers"
                                            tenants={tenants}
                                        />
                                    </FormProvider>
                                </Grid> */}

                            <Grid item xs={12} sm={6} lg={3} sx={{ marginTop: "auto", paddingTop: 2 }}>
                                <Controller
                                    control={searchExpiredPracticesForm.control}
                                    name="hideClosed"
                                    render={({ field }) => (
                                        <FormControlLabel
                                            sx={{ margin: "auto" }}
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    sx={{ marginLeft: "8px" }}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                    }}
                                                    name="hideClosed"
                                                />
                                            }
                                            label={t('searchExpired.form.hideClosed')}
                                            labelPlacement="start"
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={3} sx={{ marginTop: "auto", paddingTop: 2 }}>
                                <Controller
                                    control={searchExpiredPracticesForm.control}
                                    name="hideClosedLawsuit"
                                    render={({ field }) => (
                                        <FormControlLabel
                                            sx={{ margin: "auto" }}
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    sx={{ marginLeft: theme.spacing(1) }}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                    }}
                                                    name="hideClosedLawsuit"
                                                />
                                            }
                                            label={t('searchExpired.form.hideClosedLawsuit')}
                                            labelPlacement="start"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </FormProvider>
            </Paper>
        </form>
    )
}

export default ExpiredPracticesListFilter;